import { ScopeConfiguration, ScopeVersion, StatusType } from '@app/core/model/scope-version'
import {
  ScopeOverviewLoadingState,
  ScopeOverviewState,
} from '@app/features/scope-overview/model/scope-overview.state.model'
import { State, getScopeOverviewState } from '@app/features/scope-overview/store/reducers/index'

import { Activities } from '@app/features/scope-overview/model/activity.model'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { OfficeLocation } from '@core/model/office-location'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { SecondParty } from '@core/model/second-party.model'
import { SowRetainedHoursStats } from '@app/features/scope-overview/model/sow-retained-hours-stats.model'
import { SowRetainedTeamStats } from '@app/features/scope-overview/model/sow-retained-team-stats.model'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { createSelector } from '@ngrx/store'
import { plainToInstance } from 'class-transformer'

const getFeatureState = createSelector(getScopeOverviewState, (state: State): ScopeOverviewState => state.scopeOverview)

/** Loading State */
const getLoadingState = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeOverviewLoadingState => state.loading
)
const selectCurrentScopeLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.scope
)
const selectUpdateScopeDetailsLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.updateScopeDetails
)
const selectAllTasksLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.allTasks
)
const selectAddScopeDeliverableLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.addScopeDeliverable
)
const selectAddScopeSectionLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.addScopeSection
)

/** Loaded State */
const getLoadedState = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeOverviewLoadingState => state.loaded
)
const selectCurrentScopeLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.scope
)
const selectUpdateScopeDetailsLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.updateScopeDetails
)
const selectLibraryTemplateLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.libraryTemplate
)
const selectUpdateDeliverableLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.updateDeliverable
)
const selectAllFeesLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.allFees
)
const selectAllThirdPartyCostsLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.allThirdPartyCosts
)

/** State */
const selectCurrentScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeVersion => plainToInstance(ScopeVersion, state.currentScope)
)
const selectCurrentScopeConfiguration = createSelector(
  selectCurrentScope,
  (currentScope: ScopeVersion): ScopeConfiguration => currentScope?.configuration
)

const selectActivities = createSelector(getFeatureState, (state: ScopeOverviewState): Activities => state.activities)

const selectScopeRetainedHourStats = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeRetainedHoursStats => state.scopeRetainedHourStats
)

const selectSowRetainedHourStats = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): SowRetainedHoursStats => state.sowRetainedHourStats
)

const selectThirdPartyCostBalance = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): Money => state.thirdPartyCostBalance
)

const selectDeliverableLibraryTemplate = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.deliverableLibraryTemplate
)

const selectDeliverableLibraryEntry = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.deliverableLibraryEntry
)

const selectOutputTemplate = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.outputTemplate)

const selectDocxPreview = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.docxPreview)

const selectPreviewError = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.previewError)

const selectSecondPartyForScope = createSelector(getFeatureState, (state): SecondParty => state.secondParty)

const selectOfficeLocationForScope = createSelector(getFeatureState, (state): OfficeLocation => state.officeLocation)

const selectCustomFieldsOutdated = createSelector(getFeatureState, (state): boolean => state.customFieldsOutdated)

const selectDefaultRateCard = createSelector(getFeatureState, (state): RatecardVersion => state.defaultRateCard)

const selectRetainedTeamStats = createSelector(
  getFeatureState,
  (state): SowRetainedTeamStats[] => state.sowRetainedTeamStats
)

const selectAllRateCards = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): RatecardVersion[] => state.rateCards
)

const selectTeamError = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.teamError)

const selectFilteredDeliverables = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.filteredDeliverables
)

const selectScopeFees = createSelector(getFeatureState, (state: ScopeOverviewState) => state.currentScope.feeItems)

const getAllFees = (state: ScopeOverviewState): FeeItemInstance[] => {
  if (!state.loaded.allFees) return []

  const scopeFees = state.currentScope.feeItems.map((feeItem) => ({ ...feeItem, level: { value: 'scope' } }))
  const sectionFees = state.currentScope.sections.flatMap((section) =>
    section.feeItems.map((feeItem) => ({ ...feeItem, level: { value: 'section' } }))
  )
  const deliverableFees = state.currentScope.deliverables.flatMap((deliverable) =>
    deliverable.feeItems.map((feeItem) => ({ ...feeItem, level: { value: 'deliverable' } }))
  )
  const componentFees = state.allComponentFees.map((feeItem) => ({ ...feeItem, level: { value: 'component' } })) || []

  return scopeFees.concat(sectionFees).concat(deliverableFees).concat(componentFees)
}

const selectAllFees = createSelector(getFeatureState, getAllFees)

const getAllThirdPartyCosts = (state: ScopeOverviewState): ThirdPartyCost[] => {
  if (!state.loaded.allThirdPartyCosts) return []
  const deliverableThirdPartyCosts = state.currentScope.deliverables.flatMap(
    (deliverable) => deliverable.thirdPartyCosts
  )

  return deliverableThirdPartyCosts
    .concat(state.allSectionTPCs || [])
    .concat(state.allComponentTPCs || [])
    .concat(state.currentScope.thirdPartyCosts || [])
}
const selectAllThirdPartyCosts = createSelector(getFeatureState, getAllThirdPartyCosts)

const selectSearchedComponentsForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.scopeComponents
)

const selectSearchedNonDistinctComponentsForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.nonDistinctComponents
)

const selectSearchedDepartmentsForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.scopeDepartments
)

const selectSearchedRolesForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.scopeRoles
)

const selectDialogId = createSelector(getFeatureState, (state: ScopeOverviewState): string => state.dialogId)

const selectScenario = createSelector(getFeatureState, (state: ScopeOverviewState) =>
  plainToInstance(Scenario, state.scenario)
)

const selectConfigurationHasUpdates = createSelector(
  getFeatureState,
  (state: ScopeOverviewState) => state.configurationHasUpdates
)

const selectScopeStatus = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): StatusType => state.currentScope.status
)

const selectAvailableDeliverableTypes = (currencyCode: string) =>
  createSelector(getFeatureState, (state: ScopeOverviewState): any =>
    (state.availableDeliverableTypes || [])
      .filter((d) => !d.isFixedCost || d.complexityPriceL?.currency === currencyCode)
      .sort((a, b) => (a.name.toLowerCase().trim() > b.name.toLowerCase().trim() ? 1 : -1))
  )

const selectComponentsByDeliverable = (deliverableId: number) =>
  createSelector(
    getFeatureState,
    (state): any => state.currentScope.deliverables.find((d) => d.id === deliverableId)?.components
  )

const selectDeliverable = (deliverableId: number) =>
  createSelector(getFeatureState, (state: ScopeOverviewState): any =>
    state.currentScope.deliverables.find((deliverable) => deliverable.id === deliverableId)
  )

export const ScopeOverviewSelectors = {
  selectActivities,
  selectComponentsByDeliverable,
  selectCurrentScope,
  selectCurrentScopeConfiguration,
  selectScopeRetainedHourStats,
  selectSowRetainedHourStats,
  selectThirdPartyCostBalance,
  selectAvailableDeliverableTypes,
  selectDeliverableLibraryTemplate,
  selectDeliverableLibraryEntry,
  selectOutputTemplate,
  selectDocxPreview,
  selectPreviewError,
  selectSecondPartyForScope,
  selectOfficeLocationForScope,
  selectCustomFieldsOutdated,
  selectDefaultRateCard,
  selectRetainedTeamStats,
  selectAllRateCards,
  selectTeamError,
  selectFilteredDeliverables,
  selectScopeFees,
  selectAllFees,
  selectAllThirdPartyCosts,
  selectSearchedComponentsForScope,
  selectSearchedNonDistinctComponentsForScope,
  selectSearchedDepartmentsForScope,
  selectSearchedRolesForScope,
  selectDeliverable,
  selectDialogId,
  selectScenario,
  selectConfigurationHasUpdates,
  selectScopeStatus,

  selectAllFeesLoaded,
  selectCurrentScopeLoaded,
  selectLibraryTemplateLoaded,
  selectUpdateDeliverableLoaded,
  selectAllThirdPartyCostsLoaded,
  selectUpdateScopeDetailsLoaded,

  selectUpdateScopeDetailsLoading,
  selectAllTasksLoading,
  selectCurrentScopeLoading,
  selectAddScopeDeliverableLoading,
  selectAddScopeSectionLoading,
}
